import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  message?: string;
}

/**
 * Last-resort boundary so a render error inside the iframe shows a calm message
 * instead of a blank panel. Kept dependency-free (no UIKit) so it can render
 * even if the design system itself failed to load.
 */
export class ChatErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, message: error.message };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Chat UI crashed:', error, info.componentStack);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div
          role="alert"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            padding: 24,
            textAlign: 'center',
            fontFamily: "'Noto Sans', system-ui, sans-serif",
            color: '#324155',
            gap: 8,
          }}
        >
          <strong style={{ color: '#171f29' }}>The assistant ran into a problem.</strong>
          <span style={{ fontSize: 13 }}>Please close and reopen the assistant.</span>
        </div>
      );
    }
    return this.props.children;
  }
}
